// Cross-cutting error strings (per W2.3 outcome #4 DoD).
//
// "src/i18n/errors.ts has all error strings consumed by ErrorBoundary +
//  LoadingState + ErrorState + InfoBanner (no per-page hardcoded Chinese in
//  error branches)"
//
// ErrorBoundary + ErrorState consume these defaults. Routes can override
// `title` / `description` / `retryLabel` props per their domain. The strings
// here are the cross-cutting defaults that no per-page should re-spell.

// --- ErrorBoundary / PageErrorBoundary defaults ----------------------------

export const ERROR_BOUNDARY_TITLE_TEMPLATE = (label: string | null): string =>
  label ? `${label}發生錯誤` : "頁面發生錯誤";

export const ERROR_BOUNDARY_SUBTITLE = "請稍後再試, 或返回首頁重新開始。";

export const ERROR_BOUNDARY_GENERIC_TITLE = "發生未預期的錯誤";

export const ERROR_BOUNDARY_RETRY_LABEL = "重試一次";

// --- ErrorState defaults ---------------------------------------------------

export const ERROR_STATE_DEFAULT_TITLE = "發生了一點問題";

// W54.0 B3 — was 「請稍後再試，或聯絡我們。」.
//
// The 「我們」 was the defect, not the politeness: Apple's `writing.md › Get
// started` is explicit that first-person plurals should be avoided because
// 「it may be unclear who 'we' are」, and in this product 「聯絡我們」 points at
// nobody — there is no support desk to contact. A default that names an
// unreachable destination is worse than one that names the next step.
//
// 「請稍後再試。」 is the next step, it matches the tone of the other
// constants in this file (「請稍後再試」 appears in `ERROR_BOUNDARY_SUBTITLE`
// and both API fallbacks), and it stays true for every `ErrorState` — the
// retry button is right below it, so the copy and the control agree.
export const ERROR_STATE_DEFAULT_DESCRIPTION = "請稍後再試。";

export const ERROR_STATE_DEFAULT_RETRY = "重試";

// --- API-layer fallbacks ---------------------------------------------------

//: Used when a non-2xx response carried no `error.message` to show.
//
//: This is not a hypothetical branch. A 502 from the reverse proxy during a
//: deploy answers with an HTML body, `res.json()` yields nothing, and the
//: module has to produce *something* for `ErrorState` to render. Five API
//: modules previously fell back to `res.response.statusText`, which is
//: `fetch`'s English HTTP reason phrase — so the user saw "Bad Gateway" under a
//: Chinese title, and `wishlist.ts` went further and interpolated the method and
//: path, producing `Wishlist GET /api/v1/wishes failed` on a Traditional-Chinese
//: screen. Verified on a real 393×852 render; see
//: `src/__tests__/user-visible-error-copy.guard.test.ts` for what now keeps it
//: from coming back.
//
//: The wording says the request did not complete and offers the next step,
//: rather than restating the status code, which is the operator's information
//: and appears in the `request_id` they can quote.
export const API_REQUEST_FAILED = "請求未能完成，請稍後再試。";

//: Used when a **2xx** arrived carrying no body where the OpenAPI contract
//: declares one — see `lib/api/emptyBody.ts` for why this is a separate case
//: from `API_REQUEST_FAILED` rather than a special case of it.
//:
//: The status was a success, so the copy must not say the request failed; what
//: actually happened is that the server answered 「成功」 and then sent nothing.
//: The user's next move is the same as for any other load failure (retry), so
//: it reuses that shape while naming the real cause.
export const API_RESPONSE_EMPTY = "伺服器回傳了不完整的資料，請稍後再試。";

/** `ApiError.code` for {@link API_RESPONSE_EMPTY}. Stable across all 9 API modules. */
export const EMPTY_RESPONSE_BODY_CODE = "EMPTY_RESPONSE_BODY";

// --- Route-banner (InfoBanner) defaults ------------------------------------

export const INFO_BANNER_DIARY_LOAD_FAILED = "日記載入失敗, 請稍後再試。";

export const INFO_BANNER_PAIR_LOAD_FAILED = "配對資料載入失敗, 請稍後再試。";

// --- LoadingState — no built-in Chinese (skeleton-only) -------------------

// (intentionally empty — LoadingState renders skeleton shapes, not text)
